/**
 * AddSubTenantModal — modal para adicionar sub-tenant a um tenant SaaS
 * existente.
 *
 * Backend: POST /v1/admin/tenants/{tenantId}/add-sub-tenant
 *   Body: { name, environments: ['DEV','QA','HML','PRD','SANDBOX',...] }
 *
 * v1.32.0: ganhou checkbox "Outro" — usuario digita um env custom (regex
 * `^[A-Z][A-Z0-9_-]*$`, 2-8 chars, blocklist de reservados). Sugestoes
 * (chips) preenchem o input em 1 clique. Backend valida via Pydantic
 * EnvName (espelha ENV_NAME_REGEX no client).
 */

import { useState } from 'react'
import {
  Modal,
  Stack,
  TextInput,
  Checkbox,
  Group,
  Button,
  Text,
  Box,
  Badge,
  Tooltip,
} from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import {
  awsOrgProvisioningApi,
  STANDARD_ENV_OPTIONS,
  ENV_SUGGESTIONS,
  ENV_NAME_REGEX,
  ENV_NAME_MAX_LENGTH,
  RESERVED_ENV_NAMES,
  PROJECT_NAME_REGEX,
  type EnvName,
} from '@/api/awsOrgProvisioning'
import { handleProvisioningActionError } from './handleProvisioningActionError'

/** Sentinel value usado pelo Checkbox.Group para representar a opcao
 * "Outro". Substituido pelo nome digitado antes do submit. */
const CUSTOM_ENV_SENTINEL = '__CUSTOM__'

interface AddSubTenantModalProps {
  tenantId: number
  opened: boolean
  onClose: () => void
  onSuccess?: () => void
}

function validateCustomEnv(value: string): string | null {
  if (!value) return null
  const upper = value.toUpperCase()
  if (upper.length < 2) {
    return `Use no mínimo 2 caracteres.`
  }
  if (upper.length > ENV_NAME_MAX_LENGTH) {
    return `Use no máximo ${ENV_NAME_MAX_LENGTH} caracteres.`
  }
  if (!ENV_NAME_REGEX.test(upper)) {
    return 'Apenas A-Z, 0-9, hífen e underscore. Comece com letra.'
  }
  if (STANDARD_ENV_OPTIONS.includes(upper as never)) {
    return `Use o checkbox de ${upper} acima — não precisa digitar.`
  }
  if (RESERVED_ENV_NAMES.includes(upper)) {
    return `Nome reservado pela AWS/convenção interna.`
  }
  return null
}

export function AddSubTenantModal({
  tenantId,
  opened,
  onClose,
  onSuccess,
}: AddSubTenantModalProps) {
  const [name, setName] = useState('')
  const [envs, setEnvs] = useState<string[]>([])
  const [customEnv, setCustomEnv] = useState('')

  const reset = () => {
    setName('')
    setEnvs([])
    setCustomEnv('')
  }

  const nameValid = PROJECT_NAME_REGEX.test(name)
  const nameError =
    name.length > 0 && !nameValid
      ? 'Use apenas A-Z e 0-9, comece com letra.'
      : null

  const customSelected = envs.includes(CUSTOM_ENV_SENTINEL)
  const customEnvUpper = customEnv.toUpperCase()
  const customEnvError = customSelected
    ? validateCustomEnv(customEnvUpper)
    : null
  const customEnvValid =
    !customSelected || (customEnvUpper.length >= 2 && customEnvError === null)

  const standardSelected = envs.filter(
    (e) => e !== CUSTOM_ENV_SENTINEL,
  )
  const hasAtLeastOneEnv =
    standardSelected.length > 0 || (customSelected && customEnvValid)

  const formValid = nameValid && hasAtLeastOneEnv && customEnvValid

  const buildEnvironments = (): EnvName[] => {
    const out: EnvName[] = [...standardSelected]
    if (customSelected && customEnvUpper) {
      out.push(customEnvUpper)
    }
    return out
  }

  const mutation = useMutation({
    mutationFn: () =>
      awsOrgProvisioningApi.addSubTenant(tenantId, {
        name,
        environments: buildEnvironments(),
      }),
    onSuccess: () => {
      notifications.show({
        color: 'teal',
        title: 'Sub-tenant criado',
        message: `Sub-tenant ${name} sera provisionado em background.`,
      })
      reset()
      onSuccess?.()
      onClose()
    },
    onError: handleProvisioningActionError,
  })

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (!mutation.isPending) {
          reset()
          onClose()
        }
      }}
      title="Adicionar sub-tenant"
      data-testid="add-sub-tenant-modal"
      centered
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Cria uma OU sob a categoria <b>SaaS</b> e provisiona contas AWS
          para cada ambiente selecionado.
        </Text>

        <TextInput
          label="Nome do sub-tenant"
          placeholder="GETNET"
          value={name}
          onChange={(e) => setName(e.currentTarget.value.toUpperCase())}
          required
          error={nameError}
          data-testid="add-sub-tenant-name"
        />

        <Box>
          <Text size="sm" fw={500} mb={4}>
            Ambientes
          </Text>
          <Checkbox.Group
            value={envs}
            onChange={(values) => setEnvs(values as string[])}
          >
            <Group gap="lg">
              {STANDARD_ENV_OPTIONS.map((e) => (
                <Checkbox
                  key={e}
                  value={e}
                  label={e}
                  data-testid={`add-sub-tenant-env-${e}`}
                />
              ))}
              <Checkbox
                value={CUSTOM_ENV_SENTINEL}
                label="Outro"
                data-testid="add-sub-tenant-env-custom"
              />
            </Group>
          </Checkbox.Group>
        </Box>

        {customSelected && (
          <Box
            p="sm"
            style={{
              border: '1px dashed var(--mantine-color-default-border)',
              borderRadius: 8,
              background: 'var(--mantine-color-default-hover)',
            }}
          >
            <Stack gap="xs">
              <TextInput
                label="Nome do ambiente custom"
                description={`UPPERCASE, A-Z e 0-9, max ${ENV_NAME_MAX_LENGTH} caracteres.`}
                placeholder="SANDBOX"
                value={customEnv}
                onChange={(e) =>
                  setCustomEnv(e.currentTarget.value.toUpperCase())
                }
                maxLength={ENV_NAME_MAX_LENGTH}
                error={customEnvError}
                data-testid="add-sub-tenant-custom-env-input"
              />
              <Box>
                <Text size="xs" c="dimmed" mb={4}>
                  Sugestões:
                </Text>
                <Group gap={6}>
                  {ENV_SUGGESTIONS.map((s) => (
                    <Tooltip
                      key={s}
                      label={`Usar ${s}`}
                      withArrow
                      transitionProps={{ duration: 100 }}
                    >
                      <Badge
                        size="sm"
                        variant={customEnvUpper === s ? 'filled' : 'light'}
                        color="blue"
                        onClick={() => setCustomEnv(s)}
                        data-testid={`add-sub-tenant-suggestion-${s}`}
                        style={{
                          cursor: 'pointer',
                          textTransform: 'none',
                          fontWeight: 600,
                          letterSpacing: 0.3,
                        }}
                      >
                        {s}
                      </Badge>
                    </Tooltip>
                  ))}
                </Group>
              </Box>
            </Stack>
          </Box>
        )}

        <Group justify="flex-end" mt="sm">
          <Button
            variant="default"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={!formValid}
            data-testid="add-sub-tenant-confirm"
          >
            Confirmar
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
